import { TitleCasePipe } from "@angular/common";
import { Component, inject, Input, OnInit } from "@angular/core";
import { MatTooltip } from "@angular/material/tooltip";
import { AudienceTargeting, Marketplace, MatchType, StrategyTactic, TacticType } from "@front/m19-api-client";
import { SegmentEx, StrategyEx, SupportedAudienceMatchType } from "@front/m19-models";
import { SdStrategiesService, SegmentService, StrategyService } from "@front/m19-services";
import {
  IBadgeComponent,
  IButtonComponent,
  ICardComponent,
  MODAL_DATA,
  ModalRef,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  ProductSegmentModalComponent,
  ProductSegmentModalData,
} from "@m19-board/segments/product-segment-modal.component";
import {
  ModalRemoveTacticFromStrategyComponent,
  RemoveTacticModalData,
} from "@m19-board/strategies/tactic/tactic.component";
import { ICON_EDIT, ICON_TRASH_O } from "@m19-board/utils/iconsLabels";
import { UntilDestroy } from "@ngneat/until-destroy";

export interface RemoveAudienceModalData {
  accountId: string;
  marketplace: Marketplace;
  strategyId: number;
  audience: AudienceTargeting;
}

/* Modal component to delete the Audience from the Strategy */
@Component({
  templateUrl: "./remove-audience-popup.component.html",
  standalone: true,
  imports: [TranslocoDirective, IButtonComponent],
})
export class ModalRemoveAudienceFromStrategyComponent {
  protected readonly modalData = inject(MODAL_DATA) as RemoveAudienceModalData;
  modalRef = inject(ModalRef);

  constructor(
    private sdStrategiesService: SdStrategiesService,
    private toastrService: ToasterService,
    private translocoService: TranslocoService,
  ) {}

  delete() {
    this.sdStrategiesService
      .removeRemarketingAudienceFromSdStrategy(
        this.modalData.accountId,
        this.modalData.marketplace,
        this.modalData.strategyId,
        this.modalData.audience.audienceTargetId!,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(this.translocoService.translate("sd-targeting.audience_removed"));
          this.modalRef.close();
        },
        error: (error) => {
          this.toastrService.error(error, this.translocoService.translate("sd-targeting.audience_removal_failed"));
        },
      });
    this.modalRef.close();
  }
}

export interface RemoveTargetingModalData {
  accountId: string;
  strategyId: number;
  marketplace: Marketplace;
  organizationId: number;
}

@Component({
  templateUrl: "./remove-ia-powered-targeting-popup.component.html",
  standalone: true,
  imports: [TranslocoDirective, IButtonComponent],
})
export class ModalRemoveAIPoweredTargetingFromStrategyComponent {
  modalData = inject(MODAL_DATA) as RemoveTargetingModalData;
  modalRef = inject(ModalRef);

  constructor(
    private strategyService: StrategyService,
    private toastrService: ToasterService,
    private translocoService: TranslocoService,
  ) {}

  delete() {
    this.strategyService
      .updateStrategyAutoAlgoExploration(
        this.modalData.accountId,
        this.modalData.marketplace,
        this.modalData.organizationId,
        this.modalData.strategyId,
        false,
      )
      .subscribe({
        next: () => {
          this.toastrService.success(this.translocoService.translate("sd-targeting.ai-powered_targeting_removed"));
          this.modalRef.close();
        },
        error: (error) => {
          this.toastrService.error(error);
        },
      });
  }
}

@UntilDestroy()
@Component({
  selector: "app-sd-targeting",
  templateUrl: "./sd-targeting.component.html",
  styleUrls: ["./sd-targeting.component.scss"],
  standalone: true,
  imports: [TranslocoDirective, ICardComponent, IBadgeComponent, MatTooltip, TitleCasePipe],
})
export class SdTargetingComponent implements OnInit {
  readonly TacticType = TacticType;
  readonly supportedAudienceMatchType = SupportedAudienceMatchType;

  @Input() tactic?: StrategyTactic;

  @Input() audience?: AudienceTargeting;

  @Input() strategy?: StrategyEx;

  @Input() organizationId!: number;

  @Input() isReadOnly = false;

  @Input() defaultTactic = false;

  @Input() isLastTargeting?: boolean;

  segment: SegmentEx | undefined;

  constructor(
    private modalService: ModalService,
    private segmentService: SegmentService,
    private translocoService: TranslocoService,
  ) {}

  ngOnInit(): void {
    if (this.tactic?.segmentId && this.strategy) {
      this.segmentService.getSegments(this.strategy.accountId, this.strategy.marketplace).subscribe((segments) => {
        this.segment = segments.get(this.tactic!.segmentId!);
      });
    }
  }

  nbProductTargeting(): number {
    if (!this.segment) {
      return 0;
    }
    return this.segment.items.filter((x) => x.matchType == MatchType.asinSameAs).length ?? 0;
  }

  editSegment() {
    this.modalService.openModal<ProductSegmentModalData, void>(ProductSegmentModalComponent, {
      modalTitle: this.translocoService.translate("segment-manager.edit_segment_tt", [""]),
      data: {
        segment: this.segment,
        accountId: this.strategy!.accountId,
        marketplace: this.strategy!.marketplace,
        isReadOnly: this.isReadOnly,
      },
    });
  }

  // AI-powered targeting
  disableAutoAlgoExploration() {
    this.modalService.openModal<RemoveTargetingModalData, void>(ModalRemoveAIPoweredTargetingFromStrategyComponent, {
      modalTitle: this.translocoService.translate(
        "remove-ia-powered-targeting-popup.remove_ai-powered_targeting_from_strategy",
      ),
      data: {
        accountId: this.strategy!.accountId,
        strategyId: this.strategy!.strategyId,
        marketplace: this.strategy!.marketplace,
        organizationId: this.organizationId,
      },
    });
  }

  removeTacticFromStrategy() {
    this.modalService.openModal<RemoveTacticModalData, void>(ModalRemoveTacticFromStrategyComponent, {
      modalTitle: this.translocoService.translate("remove-tactic-popup.remove_targeting_from_strategy"),
      data: {
        accountId: this.strategy!.accountId!,
        marketplace: this.strategy!.marketplace!,
        strategyId: this.strategy!.strategyId!,
        tactic: this.tactic!,
        segment: this.segment!,
        isSdTargeting: true,
      },
    });
  }

  removeAudienceFromStrategy() {
    this.modalService.openModal<RemoveAudienceModalData, void>(ModalRemoveAudienceFromStrategyComponent, {
      modalTitle: this.translocoService.translate("remove-audience-popup.remove_audience_targeting_from_strategy"),
      data: {
        accountId: this.strategy!.accountId,
        marketplace: this.strategy!.marketplace,
        strategyId: this.strategy!.strategyId,
        audience: this.audience!,
      },
    });
  }

  protected readonly ICON_TRASH_O = ICON_TRASH_O;
  protected readonly ICON_EDIT = ICON_EDIT;
}
